import { useLayoutEffect, useRef, useState } from 'react';
import { animate } from 'motion/react';
import { EASE_MK } from '@/Components/Marketing/easing';
import { DIP_IN_SKIP_MS, DIP_OUT_MS, DIP_SWAP_MS } from './useDip';

/**
 * The rewind's two beats (R-4): the finished scene fades out, then the
 * fresh scene, already laid out at its first frame, fades in. Never both at
 * once (two layouts over each other read as a glitch), and next to no empty
 * plate between them: EASE_MK has the finished scene all but gone (about
 * 5%) 65ms into its 150ms curve, so it is dropped there (SWAP_MS) and the
 * fresh scene starts in, in the same render; the curve's invisible tail is
 * never waited out. Out on a curve of at most about 150ms, in over at most
 * about 250ms (the controller's ruling on the client's Revision 0 review),
 * both on EASE_MK: the same dip as a window's navigation (useDip).
 */
export const OUT_MS = DIP_OUT_MS;
export const SWAP_MS = DIP_SWAP_MS;
export const IN_MS = 250;
/** The fade-in starts this far into its curve: its first frame (opacity 0) would be the plate alone. */
export const IN_SKIP_MS = DIP_IN_SKIP_MS;
/** The whole rewind, from the finished scene to the fresh one drawn whole. */
export const REWIND_MS = SWAP_MS + IN_MS;

/**
 * The rewind of a looping vignette, drawn as a dip to the scene's first
 * frame: when the playback starts a new cycle, the scene that just finished
 * stays in place (the same instance, so nothing in it replays) and fades
 * out; at the fade's visual end (SWAP_MS) it is dropped, and the new
 * cycle's scene (fresh markup, at its opening state, laid out and held
 * hidden since the cycle began) fades in, in the same frame. Opacity only.
 *
 * `children` is the current cycle's scene; `cycle` is usePlayback's cycle.
 * Wrap only the scene: the frame, its top bar and the window's hairline
 * stay put. The first cycle, and a vignette that never loops (reduced
 * motion), render the scene alone. `data-rewind` on the fresh scene says
 * which beat it is in (`out`, `in`).
 */
export default function CycleRewind({ cycle, children }) {
    const [shown, setShown] = useState(cycle);
    const [leaving, setLeaving] = useState(null);
    const [phase, setPhase] = useState(null);

    // The scene as last committed: what the finished cycle looked like.
    const committed = useRef(children);
    useLayoutEffect(() => {
        committed.current = children;
    });

    // A new cycle: keep the finished scene, keyed as it was, so React holds
    // on to it rather than mounting it again, and start the dip.
    if (shown !== cycle) {
        setShown(cycle);
        setLeaving({ cycle: shown, scene: committed.current });
        setPhase('out');
    }

    const incoming = useRef(null);
    const outgoing = useRef(null);

    // One tracked set of timers and animations for the whole rewind, all
    // stopped if the vignette goes (or, in principle, a new rewind begins).
    const running = useRef({ timers: [], controls: new Set() });
    const stopAll = () => {
        const { timers, controls } = running.current;
        timers.forEach(clearTimeout);
        controls.forEach((control) => control.stop());
        running.current = { timers: [], controls: new Set() };
    };
    const track = (control) => {
        const { controls } = running.current;
        controls.add(control);
        control.then(() => controls.delete(control), () => {});
    };

    // The dip's clock, from the moment the new cycle is shown.
    useLayoutEffect(() => {
        if (!outgoing.current) return undefined;
        const out = animate(outgoing.current, { opacity: [1, 0] }, { duration: OUT_MS / 1000, ease: EASE_MK });
        track(out);
        running.current.timers.push(
            // All but gone: dropped (its curve's tail stopped with it), and
            // the fresh scene starts in the same render.
            setTimeout(() => {
                out.stop();
                setLeaving(null);
                setPhase('in');
            }, SWAP_MS),
            setTimeout(() => setPhase(null), REWIND_MS),
        );

        return stopAll;
    }, [shown]);

    // The fresh scene fades in (it is held at 0 until now), before the
    // frame that no longer has the finished one is painted.
    useLayoutEffect(() => {
        if (phase !== 'in' || !incoming.current) return;
        const fadeIn = animate(incoming.current, { opacity: [0, 1] }, { duration: IN_MS / 1000, ease: EASE_MK });
        fadeIn.time = IN_SKIP_MS / 1000;
        track(fadeIn);
    }, [phase]);

    return (
        <div className="grid">
            <div
                key={`cycle-${cycle}`}
                ref={incoming}
                data-cycle={cycle}
                data-rewind={phase ?? undefined}
                style={phase ? { opacity: 0 } : undefined}
                className="col-start-1 row-start-1 min-w-0"
            >
                {children}
            </div>
            {leaving && (
                <div
                    key={`cycle-${leaving.cycle}`}
                    ref={outgoing}
                    data-cycle={leaving.cycle}
                    data-leaving=""
                    className="col-start-1 row-start-1 min-w-0"
                >
                    {leaving.scene}
                </div>
            )}
        </div>
    );
}
